/**
 * #756 — the Overtake ticket's ordinary same-account exposure row.
 *
 * Its own component for two reasons, both about the clock it has to keep:
 *
 * 1. The line states how old its answer is, so something has to advance "now"
 *    between refreshes. Ticking here keeps that re-render off `GameDetailPanel`
 *    — the heaviest component on the page — and confines it to one line of text.
 * 2. `Date.now()` cannot be called during render (the React Compiler rule the
 *    whole dashboard is held to), so the current time is state, advanced from
 *    an interval, and the rendered age is a pure function of two state values.
 *
 * The row's HEIGHT is reserved whether or not there is an answer yet: it sits
 * directly above the fire control, and a preview resolving under the cursor
 * must not move that button.
 */
import { useEffect, useState } from 'react';

import { describeOrdinaryExposure } from './oscar';
import type { OscarKillPreview } from '../../hooks/useOscarBlockers';

/**
 * How often the rendered age advances. Coarser than the refresh interval on
 * purpose: the age is shown in whole seconds and only once it exceeds the
 * freshness threshold, so a finer tick would buy nothing but re-renders.
 */
const AGE_TICK_MS = 5_000;

export function OscarExposureRow(props: {
  preview: OscarKillPreview | null;
  /** Browser clock at receipt; null before any answer arrives. */
  receivedAtMs: number | null;
  /** Refreshes have stopped landing, so the answer is no longer kept current. */
  refreshFailing: boolean;
  /** Browser clock at the last attempt — the age reference until this
   *  component's own clock has ticked at least once. */
  checkedAtMs: number | null;
}) {
  const { preview, receivedAtMs, refreshFailing, checkedAtMs } = props;
  // Only ever advanced by the interval, never reset when a new answer lands:
  // a fresh receipt simply overtakes the last tick, and `Math.max` reads that
  // as an age of zero until the clock catches up. That keeps the effect free
  // of a synchronous setState and the render free of `Date.now()`.
  const [tickedAtMs, setTickedAtMs] = useState<number | null>(null);
  const referenceMs = tickedAtMs ?? checkedAtMs;
  const waiting = receivedAtMs == null;

  useEffect(() => {
    if (waiting) return;
    const tick = setInterval(() => setTickedAtMs(performance.now()), AGE_TICK_MS);
    return () => clearInterval(tick);
  }, [waiting]);

  const line =
    preview == null
      ? null
      : describeOrdinaryExposure(preview.ordinaryExposure, preview.killMode, {
          ageMs:
            receivedAtMs == null || referenceMs == null
              ? null
              : Math.max(0, referenceMs - receivedAtMs),
          refreshFailing,
        });

  return (
    <div data-testid="oscar-ordinary-exposure-row" className="h-12 overflow-y-auto">
      {line && (
        <div
          data-testid="oscar-ordinary-exposure"
          className={`flex items-center gap-2 font-mono text-[11px] ${
            line.tone === 'warn' ? 'text-amber-400' : 'text-muted-foreground'
          }`}
        >
          <span>{line.text}</span>
        </div>
      )}
    </div>
  );
}
